import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { DocDetailsPanel } from './components/doc-details-panel';
import { DocList } from './components/doc-list';
import { DocPanelHeader } from './components/doc-panel-header';
import { DocTableHeader } from './components/doc-table-header';
import { DocumentListDownloadColumn } from '../../../../../../../../../backend/src/matter/doc/doc-list-download';
import { DownloadFormat, MatterType } from '../../../../../../../../../backend/src/types/types';
import { ExportType } from '../../../../../../../../../backend/src/types/types';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackPageView } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useCreateDocumentZip } from '@/api/mutations/useCreateDocumentZip';
import { useDownloadDocumentsList } from '@/api/mutations/useDownloadDocumentsList';
import { useDownloadDocumentZip } from '@/api/mutations/useDownloadDocumentZip';
import useExportBundle from '@/api/mutations/useExportBundle';
import { useGetCaseDocIssues } from '@/api/queries/useGetCaseDocThemes';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetDocBundleItems } from '@/api/queries/useGetDocBundleItems';
import { useGetDocs, useGetDocsCount, useGetDocIds } from '@/api/queries/useGetDocs';
import { useGetFolderDeduplicatedCount } from '@/api/queries/useGetFolderDeduplicatedCount';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { DocumentZipFilters } from '@/api/types';
import { DocWithFacts } from '@/api/types/queries';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { useDocumentNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadFlow } from '@/new/components/download/DownloadFlow';
import { LARGE_BUNDLE_BLOCK_THRESHOLD } from '@/new/components/download/fields/large-export-warning';
import type {
  AsyncVariant,
  BundleVariant,
  DownloadDefinition,
  ZipOption,
  SyncVariant,
} from '@/new/components/download/types';
import { useDownloadController } from '@/new/components/download/useDownloadController';
import { ExportReadyDialog } from '@/new/components/export-ready-dialog';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { DocFilters } from '@/types';
import { buildDocumentFilterPills } from '@/utils/buildFilterPills';
import { parseDocStateFromParams, serializeDocStateToParams } from '@/utils/docUrlState';
import { removeFilterValue } from '@/utils/filterUtils';

const documentListDownloadOptions = Object.values(DocumentListDownloadColumn);

const DOCS_VIEW_OPTIONS_STORAGE_KEY = 'docsViewOptions';

type PanelMode = 'rawDoc' | 'details';

interface DocsViewOptions {
  isFlattened: boolean;
  panelMode: PanelMode;
}

const DEFAULT_DOCS_VIEW_OPTIONS: DocsViewOptions = { isFlattened: false, panelMode: 'rawDoc' };

function readDocsViewOptions(): DocsViewOptions {
  try {
    const raw = localStorage.getItem(DOCS_VIEW_OPTIONS_STORAGE_KEY);
    if (!raw) return DEFAULT_DOCS_VIEW_OPTIONS;
    const parsed = JSON.parse(raw) as Partial<DocsViewOptions>;
    return {
      isFlattened: parsed.isFlattened === true,
      panelMode: parsed.panelMode === 'details' ? 'details' : 'rawDoc',
    };
  } catch {
    return DEFAULT_DOCS_VIEW_OPTIONS;
  }
}

function writeDocsViewOptions(options: DocsViewOptions) {
  localStorage.setItem(DOCS_VIEW_OPTIONS_STORAGE_KEY, JSON.stringify(options));
}

function useDocBundleItems({
  matterId,
  subsetId,
  selectedDocIds,
}: {
  matterId: string;
  subsetId: string;
  selectedDocIds: Set<string>;
}) {
  const docIds = useMemo(() => Array.from(selectedDocIds).sort(), [selectedDocIds]);
  const { data, isLoading, isError } = useGetDocBundleItems({ matterId, subsetId, docIds });
  const items = (data ?? []).map((entry) => ({
    id: entry.docId,
    schemaName: 'doc' as const,
    schemaId: entry.docId,
    originalName: entry.fileName,
    pageCount: entry.pageCount ?? undefined,
    dateText: entry.dateText,
  }));
  return { items, isLoading, isError };
}

export function Documents() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Persist search params to localStorage so the doc viewer "All Docs" button can restore them
  useEffect(() => {
    const key = `docsParams:${matterId}:${subsetId}`;
    const params = searchParams.toString();
    if (params) {
      localStorage.setItem(key, params);
    } else {
      localStorage.removeItem(key);
    }
  }, [searchParams, matterId, subsetId]);

  // Parse state from URL
  const { filters, pagination, sortBy, sortOrder } = useMemo(
    () => parseDocStateFromParams(searchParams),
    [searchParams],
  );

  // URL state updater function
  const updateUrlState = useCallback(
    (updates: { filters?: DocFilters; pagination?: PaginationState; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }) => {
      const newParams = serializeDocStateToParams(
        updates.filters ?? filters,
        updates.pagination ?? pagination,
        updates.sortBy ?? sortBy,
        updates.sortOrder ?? sortOrder,
      );
      setSearchParams(newParams, { replace: true });
    },
    [filters, pagination, sortBy, sortOrder, setSearchParams],
  );

  // State setters that update URL
  const setFilters = useCallback(
    (newFilters: DocFilters | ((prev: DocFilters) => DocFilters)) => {
      const resolved = typeof newFilters === 'function' ? newFilters(filters) : newFilters;
      updateUrlState({ filters: resolved, pagination: { ...pagination, pageIndex: 0 } });
    },
    [filters, pagination, updateUrlState],
  );

  const setPagination = useCallback(
    (newPagination: PaginationState) => {
      updateUrlState({ pagination: newPagination });
    },
    [updateUrlState],
  );

  const setSortBy = useCallback(
    (newSortBy: string) => {
      updateUrlState({ sortBy: newSortBy });
    },
    [updateUrlState],
  );

  const setSortOrder = useCallback(
    (newSortOrder: 'ASC' | 'DESC') => {
      updateUrlState({ sortOrder: newSortOrder });
    },
    [updateUrlState],
  );

  const clearSort = useCallback(() => {
    updateUrlState({ sortBy: '', sortOrder: 'DESC' });
  }, [updateUrlState]);

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Documents', path: 'docs' },
  });

  const [selectedDoc, setSelectedDoc] = useState<DocWithFacts | undefined>();

  const [viewOptions, setViewOptions] = useState<DocsViewOptions>(() => readDocsViewOptions());
  const { isFlattened, panelMode } = viewOptions;

  const updateViewOptions = useCallback((updates: Partial<DocsViewOptions>) => {
    setViewOptions((prev) => {
      const next = { ...prev, ...updates };
      writeDocsViewOptions(next);
      return next;
    });
  }, []);

  const setIsFlattened = useCallback(
    (next: boolean) => {
      updateViewOptions({ isFlattened: next });

      if (filters.folderId) {
        const { folderId: _, ...restFilters } = filters;
        updateUrlState({ filters: restFilters, pagination: { ...pagination, pageIndex: 0 } });
      } else {
        updateUrlState({ pagination: { ...pagination, pageIndex: 0 } });
      }
    },
    [filters, pagination, updateUrlState, updateViewOptions],
  );

  const setPanelMode = useCallback(
    (next: PanelMode) => {
      updateViewOptions({ panelMode: next });
    },
    [updateViewOptions],
  );

  // Deep-link reconciliation: if the URL points into a folder but the persisted
  // preference is flat, the link wins — flip into folder mode so the folder shows.
  useEffect(() => {
    if (isFlattened && filters.folderId) {
      updateViewOptions({ isFlattened: false });
    }
  }, []);

  // Initialize search input from URL state
  const [globalFilter, setGlobalFilter] = useState(filters.searchQuery || '');
  const [isProcessingNL, setIsProcessingNL] = useState(false);

  // Sync search input when URL changes (browser back/forward)
  useEffect(() => {
    setGlobalFilter(filters.searchQuery || '');
  }, [filters.searchQuery]);

  const download = useDownloadController();
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());

  const {
    db,
    selectMultipleDocuments,
    getAllSelectedDocumentIds,
    selectDocument,
    deselectDocument,
    deselectAllDocuments,
    deselectMultipleDocuments,
  } = useIndexedDb();

  // Load selected documents from IndexedDB
  useEffect(() => {
    async function loadSelectedDocs() {
      if (!db || !subsetId) return;
      const selectedIds = await getAllSelectedDocumentIds(subsetId);
      setSelectedDocIds(new Set(selectedIds));
    }
    loadSelectedDocs();
  }, [db, subsetId, getAllSelectedDocumentIds]);

  const handlePageSizeChange = (newPageSize: number) => {
    const newPaginationState = { pageIndex: 0, pageSize: newPageSize };
    setPagination(newPaginationState);
  };

  const { data: docTypes } = useGetCaseDocTypes({ matterId, subsetId });
  const { data: docIssues } = useGetCaseDocIssues({ matterId, subsetId });
  const { data: filterOptions } = useGetFilterOptions({ matterId });

  // Build a map of field name -> display name for Relativity filter pills
  const relativityFieldDisplayNames = filterOptions?.relativityFields.fields.reduce(
    (acc, field) => {
      acc[field.fieldName] = field.displayName;
      return acc;
    },
    {} as Record<string, string>,
  );
  const {
    data: responseDocs,
    isLoading: isLoadingDocs,
    refetch: refetchDocs,
  } = useGetDocs({
    matterId,
    subsetId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    sortBy,
    sortOrder,
    filters: {
      searchQuery: filters.searchQuery ?? undefined,
      processed: filters.processed ?? undefined,
      extensions: filters.extensions,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      documentTypes: filters.documentTypes,
      documentIssues: filters.documentIssues,
      hasKeyFacts: filters.hasKeyFacts ?? undefined,
      hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
      isPleading: filters.isPleading,
      metadataFilters: filters.metadataFilters,
      docIds: filters.docIds,
      folderId: filters.folderId,
    },
    isFlattened,
  });

  // Fetch the count separately (runs in parallel, doesn't block initial page load)
  const { data: docsCountData } = useGetDocsCount({
    matterId,
    subsetId,
    pageSize: pagination.pageSize,
    filters: {
      searchQuery: filters.searchQuery ?? undefined,
      processed: filters.processed ?? undefined,
      extensions: filters.extensions,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      documentTypes: filters.documentTypes ?? undefined,
      documentIssues: filters.documentIssues,
      hasKeyFacts: filters.hasKeyFacts ?? undefined,
      hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
      isPleading: filters.isPleading,
      metadataFilters: filters.metadataFilters,
      docIds: filters.docIds,
      folderId: filters.folderId,
    },
    isFlattened,
  });

  const { data: filteredDocIdsData, isLoading: isAllDocIdsLoading } = useGetDocIds({
    matterId,
    subsetId,
    filters: {
      searchQuery: filters.searchQuery ?? undefined,
      processed: true, // always processed for selectable ids
      extensions: filters.extensions,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      documentTypes: filters.documentTypes,
      documentIssues: filters.documentIssues,
      hasKeyFacts: filters.hasKeyFacts ?? undefined,
      hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
      isPleading: filters.isPleading,
      metadataFilters: filters.metadataFilters,
      docIds: filters.docIds,
      folderId: filters.folderId,
    },
    isFlattened,
  });

  const folders = responseDocs?.folders;

  const handleFolderClick = useCallback(
    (folderId: string) => {
      updateUrlState({
        filters: { ...filters, folderId },
        pagination: { ...pagination, pageIndex: 0 },
      });
    },
    [filters, pagination, updateUrlState],
  );

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useDocumentNaturalLanguageFilters({
    matterId,
    subsetId,
    onFiltersChange: (newFilters: DocFilters) => {
      setFilters(newFilters);
    },
    onSearchChange: (searchQuery: string) => {
      setGlobalFilter(searchQuery);
      updateUrlState({
        filters: { ...filters, searchQuery: searchQuery || undefined },
        pagination: { ...pagination, pageIndex: 0 },
      });
    },
  });

  const onSearchCall = (searchValue?: string, useNaturalLanguage?: boolean) => {
    const value = searchValue ?? globalFilter;

    if (useNaturalLanguage && value.trim()) {
      setIsProcessingNL(true);

      buildFiltersWithNL(value);

      return;
    }

    // Update search query in URL state and reset to first page
    const newSortBy = value.trim() !== '' ? 'chunkMatchCount' : sortBy;
    const newSortOrder = value.trim() !== '' ? 'DESC' : sortOrder;
    updateUrlState({
      filters: { ...filters, searchQuery: value || undefined },
      pagination: { ...pagination, pageIndex: 0 },
      sortBy: newSortBy,
      sortOrder: newSortOrder,
    });
  };

  useEffect(() => {
    if (!isProcessingNLFiltersQuery) {
      setIsProcessingNL(false);
    }
  }, [isProcessingNLFiltersQuery]);

  const clearSearch = () => {
    setGlobalFilter('');
    setIsProcessingNL(false);

    // Clear search query and chunk match count sort
    const newSortBy = sortBy === 'chunkMatchCount' ? '' : sortBy;
    const newSortOrder = sortBy === 'chunkMatchCount' ? 'DESC' : sortOrder;
    updateUrlState({
      filters: { ...filters, searchQuery: undefined },
      sortBy: newSortBy,
      sortOrder: newSortOrder,
    });
  };

  const handleRemoveFilter = (filterKey: string, value?: unknown) => {
    // Handle metadata filters by index
    if (filterKey === 'metadataFilters' && typeof value === 'number') {
      setFilters((prev) => {
        const newMetadataFilters = prev.metadataFilters?.filter((_, i) => i !== value);
        return {
          ...prev,
          metadataFilters: newMetadataFilters?.length ? newMetadataFilters : undefined,
        };
      });
      return;
    }

    setFilters((prev) => removeFilterValue(prev, filterKey as keyof DocFilters, value));
  };

  const handleClearAllFilters = () => {
    // Preserve folder navigation when clearing content filters
    setFilters(filters.folderId ? { folderId: filters.folderId } : {});
    setGlobalFilter('');
    // Note: setFilters already resets pagination to page 0
  };

  // Document selection handlers
  const handleToggleAllDocs = async ({ checked: toggleValue }: { checked: boolean }): Promise<void> => {
    if (!subsetId) return;

    if (toggleValue) {
      if (!filteredDocIdsData?.docIds) return;
      await selectMultipleDocuments(subsetId, filteredDocIdsData.docIds);
      const selectedIds = await getAllSelectedDocumentIds(subsetId);
      setSelectedDocIds(new Set(selectedIds));
    } else {
      await deselectAllDocuments(subsetId);
      const selectedIds = await getAllSelectedDocumentIds(subsetId);
      setSelectedDocIds(new Set(selectedIds));
    }
  };

  const handleDocSelectionChange = useCallback(
    async (docId: string, selected: boolean) => {
      // Optimistic update for responsive UI
      setSelectedDocIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(docId);
        } else {
          next.delete(docId);
        }
        return next;
      });

      // Persist to IndexedDB with rollback on error
      try {
        if (selected) {
          await selectDocument(subsetId, docId);
        } else {
          await deselectDocument(subsetId, docId);
        }
      } catch (error) {
        // Rollback on error
        setSelectedDocIds((prev) => {
          const next = new Set(prev);
          if (selected) {
            next.delete(docId);
          } else {
            next.add(docId);
          }
          return next;
        });
        console.error('Failed to update document selection:', error);
      }
    },
    [subsetId, selectDocument, deselectDocument],
  );

  const handleAddSelectedDocIds = useCallback(
    async (docIds: string[]) => {
      if (!db || !subsetId) {
        throw new Error('Document selection store is not ready');
      }

      await selectMultipleDocuments(subsetId, docIds);
      const selectedIds = await getAllSelectedDocumentIds(subsetId);
      setSelectedDocIds(new Set(selectedIds));
    },
    [db, subsetId, selectMultipleDocuments, getAllSelectedDocumentIds],
  );

  const toggleAllDocsHandler = async () => {
    if (allDocsOnPageChecked) {
      await handleToggleAllDocs({ checked: false });
    } else {
      await handleToggleAllDocs({ checked: true });
    }
  };

  const deselectAllDocsFromDb = useCallback(() => {
    if (!subsetId) return;
    deselectAllDocuments(subsetId);
    setSelectedDocIds(new Set());
  }, [subsetId, deselectAllDocuments]);

  const deselectDocsFromDb = useCallback(
    (docIds: string[]) => {
      if (!subsetId || docIds.length === 0) return;
      deselectMultipleDocuments(subsetId, docIds);
      setSelectedDocIds((prev) => {
        const next = new Set(prev);
        docIds.forEach((id) => next.delete(id));
        return next;
      });
    },
    [subsetId, deselectMultipleDocuments],
  );

  // Check if all selectable docs matching the current filter scope are selected
  const allDocsOnPageChecked = useMemo(() => {
    const ids = filteredDocIdsData?.docIds;
    if (!ids || ids.length === 0) {
      return false;
    }
    return ids.every((id) => selectedDocIds.has(id));
  }, [filteredDocIdsData?.docIds, selectedDocIds]);

  useEffect(() => {
    refetchDocs();
  }, [pagination, filters, refetchDocs]);

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const { fetchDownloadAsync, writtenMutation, downloadExport } = useDownloadDocumentsList({ matterId, subsetId });
  const { bundleEndpoints, downloadExport: downloadBundleExport } = useExportBundle();
  const { mutateAsync: writtenMutateAsync } = writtenMutation;
  // Capture `name` once — ExportReadyDialog strips query params on mount.
  const bundleNameRef = useRef(new URLSearchParams(window.location.search).get('name'));
  const { createZipAsync } = useCreateDocumentZip({ matterId, subsetId });
  const { downloadZip } = useDownloadDocumentZip({ matterId, subsetId });

  const buildZipFilters = useCallback((): DocumentZipFilters | undefined => {
    const hasFilters =
      filters.searchQuery ||
      filters.processed !== undefined ||
      (filters.extensions?.length ?? 0) > 0 ||
      filters.startDate ||
      filters.endDate ||
      (filters.documentTypes?.length ?? 0) > 0 ||
      (filters.documentIssues?.length ?? 0) > 0 ||
      filters.hasKeyFacts !== undefined ||
      filters.hasRelevantFacts !== undefined ||
      filters.isPleading !== undefined ||
      (filters.metadataFilters?.length ?? 0) > 0;

    if (!hasFilters) return undefined;

    return {
      searchQuery: filters.searchQuery ?? undefined,
      processed: filters.processed ?? undefined,
      extensions: filters.extensions,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      documentTypes: filters.documentTypes,
      documentIssues: filters.documentIssues,
      hasKeyFacts: filters.hasKeyFacts ?? undefined,
      hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
      isPleading: filters.isPleading ?? undefined,
      metadataFilters: filters.metadataFilters,
    };
  }, [filters]);

  const zipFilters = buildZipFilters();
  const hasActiveFilters = !!zipFilters;
  const subsetName = subset?.name || 'Documents';

  const downloadDefinition = useMemo<DownloadDefinition>(() => {
    const zipProgressEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/docs/zip-progress/${subsetId}`;
    const zipStatusEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/docs/zip-status/${subsetId}`;
    const zipDownloadReady = async (exportId: string, _format: unknown, filename?: string) => {
      await new Promise<void>((resolve, reject) => {
        downloadZip(
          { exportId, filename: filename || subsetName },
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          },
        );
      });
    };
    const startZipExport = async (zf: DocumentZipFilters | undefined) => {
      const data = await createZipAsync(zf);
      if (!data.exportId) throw new Error('Failed to start zip creation');
      return { exportId: data.exportId, jobId: data.jobId };
    };

    const hasSelection = selectedDocIds.size > 0;
    const scopes: DownloadDefinition['scopes'] = [];
    if (hasSelection) {
      scopes.push({
        id: 'selected',
        label: `Download ${selectedDocIds.size} selected document${selectedDocIds.size !== 1 ? 's' : ''}`,
        totalCount: selectedDocIds.size,
      });
    }
    if (hasActiveFilters) {
      scopes.push({
        id: 'filtered',
        label: 'Download only filtered documents',
        totalCount: responseDocs?.totalCount,
      });
    }
    scopes.push({
      id: 'all',
      label: 'Download all documents',
      totalCount: docsCountData?.totalCount,
    });

    const defaultScopeId = hasSelection ? 'selected' : hasActiveFilters ? 'filtered' : 'all';

    const resolveZipFilters = (scopeId?: string): DocumentZipFilters | undefined => {
      if (scopeId === 'selected') return { docIds: [...selectedDocIds] };
      if (scopeId === 'filtered') return zipFilters;
      return undefined;
    };

    const listVariant: SyncVariant<DownloadFormat> = {
      id: 'documents-list',
      kind: 'table',
      label: 'Table',
      description: 'Word or Excel',
      mode: 'sync',
      formats: [DownloadFormat.EXCEL, DownloadFormat.DOCX, DownloadFormat.CSV],
      defaultFilename: subsetName,
      columns: {
        available: documentListDownloadOptions,
        default: documentListDownloadOptions,
        relativity: filterOptions?.relativityFields?.fields,
      },
      largeExportWarning: true,
      mutate: async (values) => {
        await fetchDownloadAsync({
          title: values.filename,
          columns: values.columns ?? [],
          relativityColumns: values.relativityColumns?.length ? values.relativityColumns : undefined,
          format: values.format,
          searchQuery: filters.searchQuery ?? undefined,
          processed: filters.processed ?? undefined,
          extensions: filters.extensions,
          startDate: filters.startDate ?? undefined,
          endDate: filters.endDate ?? undefined,
          documentTypes: filters.documentTypes,
          documentIssues: filters.documentIssues,
          hasKeyFacts: filters.hasKeyFacts ?? undefined,
          hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
          isPleading: filters.isPleading ?? undefined,
          metadataFilters: filters.metadataFilters,
        });
      },
    };

    const listProgressEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/docs/list-export-progress/${subsetId}`;
    const listStatusEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/docs/list-export-status/${subsetId}`;
    const listDownloadReady = async (exportId: string, format?: DownloadFormat, filename?: string) => {
      await downloadExport(filename || subsetName, exportId, format);
    };

    const writtenVariant: AsyncVariant<DownloadFormat> = {
      id: 'documents-written',
      kind: 'written',
      label: 'Written',
      description: 'Word',
      mode: 'async',
      formats: [DownloadFormat.DOCX],
      defaultFilename: subsetName,
      style: true,
      styleItemNoun: 'document',
      docOptions: true,
      largeExportWarning: true,
      mutate: async (values) => {
        const { exportId, jobId } = await writtenMutateAsync({
          title: values.filename,
          style: values.style,
          format: values.format as DownloadFormat,
          documentOptions: values.docOptions,
        });
        return { exportId, jobId };
      },
      progressEndpoint: listProgressEndpoint,
      statusEndpoint: listStatusEndpoint,
      downloadReady: listDownloadReady,
    };

    const selectedDocCount = selectedDocIds.size;
    const bundleVariant: BundleVariant = {
      id: 'docs-bundle',
      kind: 'bundle',
      label: 'PDF Bundle',
      description: 'Paginated PDFs from selected documents',
      disabledReason:
        selectedDocCount === 0
          ? 'Select one or more documents to bundle.'
          : selectedDocCount > LARGE_BUNDLE_BLOCK_THRESHOLD
            ? `Bundles are limited to ${LARGE_BUNDLE_BLOCK_THRESHOLD.toLocaleString()} documents (you have ${selectedDocCount.toLocaleString()} selected).`
            : undefined,
      bundle: {
        matterId,
        subsetId,
        itemType: 'doc',
        useItems: () => useDocBundleItems({ matterId, subsetId, selectedDocIds }),
        defaultName: `${subset?.name ?? 'Documents'} Bundle`,
      },
      ...bundleEndpoints(matterId, subsetId),
      downloadReady: (exportId, name) =>
        downloadBundleExport(matterId, subsetId, name ?? `${subset?.name ?? 'Documents'} Bundle`, exportId),
      readyDescription: 'Your PDF bundle is ready.',
      downloadButtonText: 'Download Bundle',
    };

    const variants = [
      listVariant,
      writtenVariant,
      {
        id: 'documents-zip',
        kind: 'zip' as const,
        label: 'Zip',
        description: 'Archive of selected documents.',
        mode: 'async' as const,
        formats: ['zip' as const],
        defaultFilename: subsetName,
        showZipContent: true,
        largeExportWarning: true,
        mutate: (values: { scope?: string; zipContent?: ZipOption }) => {
          const base = resolveZipFilters(values.scope) ?? {};
          return startZipExport({ ...base, zipContent: values.zipContent });
        },
        progressEndpoint: zipProgressEndpoint,
        statusEndpoint: zipStatusEndpoint,
        downloadReady: zipDownloadReady,
        readyDescription: `Documents have been packaged as a zip.`,
        downloadButtonText: `Download ${subsetName}.zip`,
      },
      bundleVariant,
    ];

    return {
      scopes,
      defaultScopeId,
      variants,
      tracking: {
        event: TrackingEventsV2.DocumentDownload,
        basePayload: { matterId, subsetId, isAllDocuments: subset?.isAllDocuments },
      },
    };
  }, [
    matterId,
    subsetId,
    subsetName,
    hasActiveFilters,
    zipFilters,
    filters,
    filterOptions?.relativityFields?.fields,
    subset?.isAllDocuments,
    subset?.name,
    selectedDocIds,
    responseDocs?.docs,
    responseDocs?.totalCount,
    docsCountData?.totalCount,
    fetchDownloadAsync,
    writtenMutateAsync,
    downloadExport,
    createZipAsync,
    downloadZip,
    bundleEndpoints,
    downloadBundleExport,
  ]);

  const handleFolderDownloadZip = useCallback(
    async (folderId: string) => {
      const data = await createZipAsync({ folderId });
      if (!data.exportId) return;
      download.startAsync({
        variantId: 'documents-zip',
        exportId: data.exportId,
        jobId: data.jobId,
        format: 'zip',
      });
    },
    [createZipAsync, download],
  );

  const fetchFolderDeduplicatedCount = useGetFolderDeduplicatedCount();
  const [folderDedupPrompt, setFolderDedupPrompt] = useState<{ folderId: string; count: number } | null>(null);

  const navigateToFolderSubsetCreator = useCallback(
    (folderId: string, includeDedup: boolean) => {
      const includeParam = includeDedup ? '&includeFolderDedup=1' : '';
      navigate(`/matters/${matterId}/subsets/new?folderId=${folderId}${includeParam}`);
    },
    [matterId, navigate],
  );

  const handleFolderCreateSubset = useCallback(
    async (folderId: string) => {
      try {
        const { count } = await fetchFolderDeduplicatedCount(matterId, folderId);
        if (count > 0) {
          setFolderDedupPrompt({ folderId, count });
          return;
        }
      } catch {
        // If the count check fails, fall through to the existing behaviour.
      }
      navigateToFolderSubsetCreator(folderId, false);
    },
    [fetchFolderDeduplicatedCount, matterId, navigateToFolderSubsetCreator],
  );

  useEffect(() => {
    if (responseDocs && responseDocs.docs && responseDocs.docs.length > 0) {
      // Preserve currently selected doc if it still exists in the list (e.g. after a refetch)
      const current = selectedDoc ? responseDocs.docs.find((doc) => doc.id === selectedDoc.id) : undefined;
      setSelectedDoc(current ?? responseDocs.docs[0]);
    } else {
      setSelectedDoc(undefined);
    }
  }, [responseDocs]);

  useEffect(() => {
    trackPageView(PageName.Documents);
  }, []);

  // Keyboard navigation for documents
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!responseDocs?.docs || responseDocs.docs.length === 0) return;

      const currentIndex = selectedDoc ? responseDocs.docs.findIndex((doc) => doc.id === selectedDoc.id) : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex < responseDocs.docs.length - 1 ? currentIndex + 1 : currentIndex;
        if (nextIndex >= 0) {
          setSelectedDoc(responseDocs.docs[nextIndex]);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        setSelectedDoc(responseDocs.docs[prevIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [responseDocs, selectedDoc]);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />
      <div className="flex-1 overflow-hidden">
        <div className="flex h-full w-full flex-col text-sm">
          <DocTableHeader
            matterId={matterId}
            subsetId={subsetId}
            isAllDocuments={subset?.isAllDocuments}
            matterIssues={matter?.issues}
            isMatterDraft={matter?.isDraft}
            isSampleMatter={matter?.type === MatterType.Sample}
            onOpenDownload={download.open}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            onSearchCall={onSearchCall}
            clearSearch={clearSearch}
            processingNL={isProcessingNL}
            currentPage={pagination.pageIndex}
            prevPage={prevPage}
            nextPage={nextPage}
            goToPage={goToPage}
            pageSize={pagination.pageSize}
            onPageSizeChange={handlePageSizeChange}
            hasActiveFilters={hasActiveFilters}
            totalPages={docsCountData?.pageCount ?? responseDocs?.pageCount}
            hasMore={responseDocs?.hasMore}
            totalCount={docsCountData?.totalCount ?? responseDocs?.totalCount}
            totalUnfilteredCount={responseDocs?.totalUnfilteredCount}
            filterPills={buildDocumentFilterPills(filters, handleRemoveFilter, relativityFieldDisplayNames)}
            onClearAllFilters={handleClearAllFilters}
            filters={filters}
            setFilters={setFilters}
            allDocumentTypes={docTypes ?? []}
            docIssues={docIssues ?? []}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            clearSort={clearSort}
            // Document selection
            selectedDocIds={selectedDocIds}
            allDocsOnPageChecked={allDocsOnPageChecked}
            toggleAllDocsHandler={toggleAllDocsHandler}
            deselectAllDocsFromDb={deselectAllDocsFromDb}
            deselectDocsFromDb={deselectDocsFromDb}
            onAddSelectedDocIds={handleAddSelectedDocIds}
            isAllDocIdsLoading={isAllDocIdsLoading}
            isLoadingDocs={isLoadingDocs}
            isFlattened={isFlattened}
            setIsFlattened={setIsFlattened}
          />

          <div className="flex flex-1 flex-row divide-x overflow-y-scroll">
            <div className="flex max-w-2/5 min-w-2/5 flex-1 flex-col overflow-x-hidden overflow-y-scroll">
              <DocList
                docs={responseDocs?.docs}
                folders={folders}
                isLoading={isLoadingDocs}
                searchQuery={filters.searchQuery ?? ''}
                selectedDoc={selectedDoc}
                setSelectedDoc={setSelectedDoc}
                selectedDocIds={selectedDocIds}
                onDocSelectionChange={handleDocSelectionChange}
                onFolderClick={handleFolderClick}
                isInsideFolder={!!filters.folderId}
                hasContentFilters={hasActiveFilters}
                isFlattened={isFlattened}
                currentFolderPath={responseDocs?.currentFolderPath}
                onNavigateUp={() => {
                  const parentId = responseDocs?.parentFolderId;
                  if (parentId) {
                    updateUrlState({
                      filters: { ...filters, folderId: parentId },
                      pagination: { ...pagination, pageIndex: 0 },
                    });
                  } else {
                    setFilters((prev) => {
                      const { folderId: _, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                onFolderDownloadZip={handleFolderDownloadZip}
                onFolderCreateSubset={handleFolderCreateSubset}
              />
            </div>
            <div className="flex min-w-0 grow flex-col overflow-hidden">
              {selectedDoc ? (
                <div className="flex h-full flex-col">
                  <DocPanelHeader
                    doc={selectedDoc}
                    matterId={matterId}
                    subsetId={subsetId}
                    panelMode={panelMode}
                    setPanelMode={setPanelMode}
                  />
                  {panelMode === 'details' ? (
                    <div className="min-h-0 flex-1 overflow-y-auto">
                      <DocDetailsPanel doc={selectedDoc} />
                    </div>
                  ) : (
                    <div className="relative min-h-0 flex-1">
                      <PDFViewer
                        key={selectedDoc.id}
                        docId={selectedDoc.id}
                        highlights={[]}
                        hideFeatureButtons
                        fileName={selectedDoc.file?.name}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-center">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Icon className="lucide--file-question-mark size-3.5" />
                    <p className="text-sm">Select a document to view it</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DownloadFlow controller={download} definition={downloadDefinition} />

          <ExportReadyDialog
            fallbackHandlerKey={ExportType.DocZip}
            handlers={{
              [ExportType.DocZip]: {
                message: 'Your document zip is ready to download.',
                onDownload: async (exportId) => {
                  downloadZip({ exportId, filename: subset?.name || 'Documents' });
                },
              },
              [ExportType.Bundle]: {
                message: 'Your PDF bundle is ready to download.',
                onDownload: async (exportId) => {
                  const name = bundleNameRef.current ?? `${subset?.name ?? 'Documents'} Bundle`;
                  await downloadBundleExport(matterId, subsetId, name, exportId);
                },
              },
              [ExportType.DocList]: {
                message: 'Your document list export is ready to download.',
                onDownload: async (exportId, format) => {
                  // Older email URLs (pre-format-param-fix) omit `format`.
                  // The async docList path only emits DOCX today, so default
                  // there. TODO: remove fallback after legacy emails expire.
                  await downloadExport('Documents', exportId, format ?? DownloadFormat.DOCX);
                },
              },
            }}
          />

          <Dialog
            open={folderDedupPrompt !== null}
            onOpenChange={(open) => {
              if (!open) setFolderDedupPrompt(null);
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Include deduplicated files?</DialogTitle>
              </DialogHeader>
              <DialogDescription className="pt-2 text-sm text-gray-600">
                {folderDedupPrompt?.count === 1
                  ? '1 file was uploaded into this folder but rejected as a duplicate of an existing file.'
                  : `${folderDedupPrompt?.count ?? 0} files were uploaded into this folder but rejected as duplicates of existing files.`}{' '}
                Would you like to include {folderDedupPrompt?.count === 1 ? 'it' : 'them'} in the new Subset?
              </DialogDescription>
              <DialogFooter className="mt-4 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!folderDedupPrompt) return;
                    const { folderId } = folderDedupPrompt;
                    setFolderDedupPrompt(null);
                    navigateToFolderSubsetCreator(folderId, false);
                  }}
                >
                  Skip
                </Button>
                <Button
                  onClick={() => {
                    if (!folderDedupPrompt) return;
                    const { folderId } = folderDedupPrompt;
                    setFolderDedupPrompt(null);
                    navigateToFolderSubsetCreator(folderId, true);
                  }}
                >
                  Include
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
